import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Skeleton from '@material-ui/lab/Skeleton'
import clsx from 'clsx'
import React from 'react'
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable'
import ReactResizeDetector from 'react-resize-detector'
import sanitizeHtml from 'sanitize-html'

const sanitizeConfig = {
    allowedTags: ['span'],
    allowedAttributes: { span: ['style'] },
}
const stripperConfig = {
    allowedTags: [],
}

const decodeEntities = (encodedString: string) => {
    var translate_re = /&(nbsp|amp|quot|lt|gt);/g
    var translate: Record<string, string> = {
        nbsp: ' ',
        amp: '&',
        quot: '"',
        lt: '<',
        gt: '>',
    }
    return encodedString
        .replace(translate_re, function (match, entity) {
            return translate[entity]
        })
        .replace(/&#(\d+);/gi, function (match, numStr) {
            var num = parseInt(numStr, 10)
            return String.fromCharCode(num)
        })
}

interface EditableTextStyleProps {
    /**
     * Editor font size
     */
    fontSize?: number
    /**
     * Number lineheight in px
     */
    lineHeight?: number

    /**
     * Primary editor text color
     */
    color?: string
    baseHeight?: number
}

const useStyles = makeStyles({
    editableTextContainer: {
        fontSize: (props: EditableTextStyleProps) => pxToRem(props.fontSize),
        width: '100%',
    },
    editableTextContentText: {
        border: 'none',
        outline: 'none',
        lineHeight: (props: EditableTextStyleProps) => `${props.lineHeight}px`,
        width: '100%',

        '& .MuiInputBase-input.Mui-disabled': {
            color: (props: EditableTextStyleProps) => props.color,
        },
    },
    baseInput: {
        color: (props: EditableTextStyleProps) => props.color,
        lineHeight: (props: EditableTextStyleProps) => `${props.lineHeight}px`,
        outline: 'none',
        width: '100%',
        maxWidth: '100%',
    },
    textEditMode: {
        outline: '1px solid #13B5Ea',
        width: '100%',
    },
    textEditModeError: {
        outline: '1px solid #ff0000',
    },
    inputOverlay: {
        height: 47,
    },
    overlay: {
        height: 47,
        top: 0,
        zIndex: -1,
    },
    characterCountContainer: {
        color: '#fff',
        height: 20,
        backgroundColor: '#13B5EA',
        alignItems: 'center',
        alignSelf: 'flex-end',
        width: 'auto',
    },
    characterCountContainerError: {
        backgroundColor: '#ff0000',
    },
    errorMessage: {
        color: '#ff0000',
        fontStyle: 'italic',
    },
    characterCountText: {
        fontSize: pxToRem(12),
        minWidth: 61,
        textAlign: 'center',
        transition: '140ms',
        paddingLeft: 8,
        paddingRight: 8,
    },
    counterPlaceholder: {
        height: 20,
    },
    normalizedFont: {
        // It is needed to specify the font-family here because of `pre` tag.
        fontFamily: 'Roboto',
        // Removes top and bottom margin for `pre` tag
        margin: 0,
    },
})

const useEditorStyles = makeStyles({
    editorDiv: {
        [`& > div > span`]: {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            wordWrap: 'break-word',
        },
    },
})

interface EditableTextProps extends Partial<EditableTextStyleProps> {
    /**
     * Determines if the component displays edit/display mode.
     */
    editable?: boolean
    /**
     * Line limit for editor. Text beyond this line
     * will be dimmed.
     */
    limit?: number
    /**
     * Character limit, not visible to users.
     */
    charLimit?: number
    onChange?: (textContent: string) => void
    textContent: string
    onBlur?: (textContent: string) => void
    firmLoading?: boolean
    required?: boolean
}

/**
 * This is a touch-to-edit editor that implements
 * line-limited text editing. It grays out texts that are
 * beyond the given line limit, while also applying the
 * given char count limit.
 *
 * Unlike the EditableTextBlock, however, this editor
 * does not display character count.
 */
const EditableText: React.FC<EditableTextProps> = ({
    editable = true,
    onBlur,
    limit = 2,
    charLimit = 255,
    fontSize = 30,
    lineHeight = 35,
    textContent = '',
    onChange,
    color = '#000',
    firmLoading = true,
    required,
    ...props
}) => {
    const offsetCharLimit = charLimit
    const isAndroidChrome = React.useMemo(
        () =>
            navigator.userAgent.indexOf('Android') !== -1 &&
            navigator.userAgent.indexOf('Chrome') !== -1,
        []
    )
    const [lineExceedIndex, setLineExceedIndex] = React.useState(-1)
    const [contentRef, setContentRef] = React.useState<HTMLElement | null>(null)
    const [exceedsLineLimit, setExceedsLineLimit] = React.useState(false)
    const [cursorPosition, setCursorPosition] = React.useState<number>(0)
    const prevTextContent = React.useRef<string | undefined>()
    const classes = useStyles({
        fontSize,
        lineHeight,
        color,
    })
    const [content, setContent] = React.useState('')

    const editorClasses = useEditorStyles()
    /**
     * Gets cursor offset and returns it in number format.
     */
    const getCursorPosition = () => {
        const selection = window.getSelection()
        let result = 0
        if (selection!.rangeCount > 0) {
            const range = selection?.getRangeAt(0)
            const preCaret = range?.cloneRange()
            if (!contentRef) return 0
            preCaret!.selectNodeContents(contentRef as Node)
            preCaret!.setEnd(range!.endContainer, range!.endOffset)
            result = preCaret!.toString().length
        }

        return result
    }

    const positionCursor = React.useCallback(
        (stringIndex: number) => {
            if (contentRef) {
                const originalCursorPosition = stringIndex
                const selection = window.getSelection()
                const range = document.createRange()
                let nodeIndex = -1
                let charCounter = 0
                while (
                    charCounter < originalCursorPosition &&
                    nodeIndex < contentRef!.childNodes[0].childNodes.length
                ) {
                    nodeIndex++

                    const currentNode = contentRef!.childNodes[0].childNodes[
                        nodeIndex
                    ]
                    if (currentNode?.nodeType !== Node.ELEMENT_NODE) {
                        nodeIndex--
                        break
                    }
                    const wordLength =
                        contentRef!.childNodes[0].childNodes[nodeIndex]
                            ?.textContent?.length || 0

                    charCounter += wordLength
                }
                if (nodeIndex >= 0) {
                    const nodeCharCount =
                        contentRef.childNodes[0]?.childNodes[nodeIndex]
                            ?.textContent?.length || 0
                    let nodeOffset =
                        nodeCharCount - (charCounter - originalCursorPosition)
                    const currentOffsetNode =
                        contentRef.childNodes[0].childNodes[nodeIndex]
                            .childNodes[0]
                    if (isAndroidChrome) {
                        if (
                            nodeOffset >
                            (currentOffsetNode?.textContent?.length ?? 0)
                        ) {
                            nodeOffset =
                                currentOffsetNode?.textContent?.length ?? 0
                        }
                    }
                    if (currentOffsetNode instanceof Node) {
                        range.setStart(currentOffsetNode, nodeOffset)
                        range.collapse(true)
                    }
                    selection!.removeAllRanges()
                    selection!.addRange(range)
                }
            }
        },
        [contentRef, isAndroidChrome]
    )

    /**
     * Handles content change. Also applies logic for segmenting
     * words into different spans for coloration.
     */
    const handleContentChange = (newValue: ContentEditableEvent) => {
        if (content !== newValue.target.value) {
            let plainText = decodeEntities(
                sanitizeHtml(newValue.target.value, stripperConfig)
            )
            const hasHiddenChar =
                plainText.indexOf(String.fromCharCode(65279)) > -1
            if (hasHiddenChar) {
                plainText = plainText.replace(
                    new RegExp(String.fromCharCode(65279)),
                    ''
                )
            }

            if (isAndroidChrome && plainText.length > offsetCharLimit) {
                plainText = decodeEntities(
                    sanitizeHtml(content, stripperConfig)
                )
            }

            let newContent = wrapTextContents(plainText)

            if (newContent !== content) {
                if (plainText.length > offsetCharLimit) {
                    setCursorPosition(offsetCharLimit)
                } else {
                    setCursorPosition(
                        getCursorPosition() - (hasHiddenChar ? 1 : 0)
                    )
                }
                setContent(newContent)
                onChange?.(newContent)
            } else if (plainText.length > offsetCharLimit) {
                setCursorPosition(offsetCharLimit)
            } else if (plainText.length === offsetCharLimit) {
                setCursorPosition(offsetCharLimit)
                if (isAndroidChrome) {
                    setContent(newContent + '<span></span>')
                }
            }
        }
    }

    const wrapTextContents = React.useCallback(
        (plainTextContent: string) => {
            const charLimitedPlainText = plainTextContent.substr(
                0,
                offsetCharLimit
            )
            let atomizedText: string[] = []
            // Split the char-limited text into white-space separated "words"
            const wordSplitPlainText = charLimitedPlainText.split(' ')

            // Iterate through the "words". If the "word" is escaped,
            // push the entire word. Otherwise, split into single characters
            // then push.
            wordSplitPlainText.forEach((word, idx) => {
                if (word.match(/&.*;/)) {
                    atomizedText.push(word)
                } else {
                    atomizedText = atomizedText.concat(word.split(''))
                }

                // Add space after each word if it it's not the last word
                if (idx < wordSplitPlainText.length - 1) atomizedText.push(' ')
            })
            const wrappedText = atomizedText
                .map((word: string, index: number) => {
                    if (word.length > 0) {
                        const colorStyle = 'color: #aaa;'
                        const displayStyle = 'display: inline-block;'
                        let wordLength = -1
                        let finalStyle = ''

                        // Workaround for word-break issue in Edge browsers
                        if (
                            window.navigator.userAgent.indexOf('Edge') > -1 &&
                            word !== ' '
                        ) {
                            //determine if long word
                            const textBefore = charLimitedPlainText.substring(
                                0,
                                index
                            )
                            wordLength = charLimitedPlainText.length
                            let wordStartIndex = textBefore.lastIndexOf(' ')
                            let wordEndIndex = charLimitedPlainText.indexOf(
                                ' ',
                                wordStartIndex === -1 ? 0 : index
                            )
                            if (wordEndIndex !== -1) {
                                if (wordStartIndex === -1) {
                                    wordLength = wordEndIndex - 1
                                } else {
                                    wordLength =
                                        wordEndIndex -
                                        index +
                                        (index - wordStartIndex) -
                                        1
                                }
                            } else {
                                if (wordStartIndex !== -1) {
                                    wordLength =
                                        charLimitedPlainText.length -
                                        (wordStartIndex + 1)
                                }
                            }
                        }

                        if (word === ' ' || wordLength >= 16) {
                            finalStyle += displayStyle
                        }

                        if (
                            lineExceedIndex !== -1 &&
                            index >= lineExceedIndex
                        ) {
                            finalStyle += colorStyle
                            return `<span style="${finalStyle}">${word}</span>`
                        }

                        return `<span style="${finalStyle}">${word}</span>`
                    }
                    return ''
                })
                .join('')

            if (wrappedText.length <= 0) {
                return `<span>&#65279;</span>`
                // return '<br>'
            }

            const newContent = sanitizeHtml(wrappedText, sanitizeConfig)
            return newContent
        },
        [offsetCharLimit, lineExceedIndex]
    )

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        onBlur?.(contentRef?.textContent || '')
        // setEditMode(false)
    }
    /**
     * Handles keydown events and implements
     * logic for character count limit.
     */
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (contentRef) {
            // if cursor is at the end of the string and
            // editor is full, stop propagation
            const selectionCollapsed = window.getSelection()?.isCollapsed

            // determine if paste hotkey
            // const isPaste = event.ctrlKey && event.key === 'v'
            // prevent exceeding of max chars
            const exceedMaxChars =
                contentRef!.textContent!.length >= offsetCharLimit &&
                event.key.length === 1 &&
                selectionCollapsed

            // prevent insertion of linebreaks
            const isEnterKey = event.keyCode === 13

            const isSpace = event.keyCode === 32

            if (exceedMaxChars || isEnterKey) {
                if (event.ctrlKey) return false
                event.preventDefault()
                event.stopPropagation()
                return true
            }

            if (isSpace) {
                event.preventDefault()

                let currentContent = event.currentTarget.textContent
                const cursorEndPosition = getCursorPosition()
                if (window.getSelection()?.isCollapsed) {
                    window.getSelection()?.getRangeAt(0).collapse(true)
                }
                const cursorStartPosition = getCursorPosition()
                const leftContent = currentContent?.substr(
                    0,
                    cursorStartPosition
                )
                const rightContent = currentContent?.substr(cursorEndPosition)
                const newContent = leftContent + ' ' + rightContent
                const wrappedNewContent = wrapTextContents(newContent)

                setContent(wrappedNewContent)
                setCursorPosition(cursorStartPosition + 1)
                return true
            }
        }
    }

    const handleContentPaste = (
        event: React.ClipboardEvent<HTMLDivElement>
    ) => {
        event.preventDefault()
        const currentText = event.currentTarget?.textContent || ''
        const hasHiddenChar =
            currentText.indexOf(String.fromCharCode(65279)) > -1
        const hiddenCharIndex = currentText.indexOf(String.fromCharCode(65279))

        const cursorEndPosition =
            getCursorPosition() -
            (hasHiddenChar && hiddenCharIndex <= getCursorPosition() ? 1 : 0)

        if (!window.getSelection()?.isCollapsed) {
            window.getSelection()?.getRangeAt(0).collapse(true)
        }
        const cursorStartPosition =
            getCursorPosition() -
            (hasHiddenChar && hiddenCharIndex <= getCursorPosition() ? 1 : 0)

        const plainText = decodeEntities(
            sanitizeHtml(
                (content || '').replace(
                    new RegExp(String.fromCharCode(65279)),
                    ''
                ),
                stripperConfig
            )
        )

        // get remaining available chars
        const remainingCharCount =
            offsetCharLimit -
            (plainText.length - (cursorEndPosition - cursorStartPosition))

        const plainTextClipboardData = decodeEntities(
            event.clipboardData
                .getData('text')
                .replace(new RegExp(`(${String.fromCharCode(65279)})`), 'g')
        ).substr(0, remainingCharCount)

        const contentLeftPaste = plainText.substring(0, cursorStartPosition)
        const contentRightPaste = plainText.substring(cursorEndPosition)
        const contentAfterPaste =
            contentLeftPaste + plainTextClipboardData + contentRightPaste

        let newContent = wrapTextContents(decodeEntities(contentAfterPaste))

        const resultingCursorPosition =
            cursorStartPosition + plainTextClipboardData.length
        if (newContent !== content) {
            setCursorPosition(resultingCursorPosition)
            setContent(newContent)
        } else {
            positionCursor(resultingCursorPosition)
        }
    }

    /**
     * Method for going through segmented innerHTML of the editor
     * and applying gray color to texts beyond the line limit.
     */
    const applyColors = React.useCallback(() => {
        const spans = contentRef?.getElementsByTagName('span')
        const firstSpan = spans?.item(0)
        if ((spans?.length || 0) <= 0) return

        if (Math.round(contentRef!.clientHeight / lineHeight) > limit) {
            if (!exceedsLineLimit) setExceedsLineLimit(true)
            for (let i = 0; i < spans!.length; i++) {
                const curSpan = spans?.item(i)
                if (
                    Math.round(
                        (curSpan!.offsetTop - firstSpan!.offsetTop) / lineHeight
                    ) >= limit
                ) {
                    if (i < lineExceedIndex || lineExceedIndex === -1) {
                        setLineExceedIndex(i)
                        break
                    }
                } else if (i === lineExceedIndex) {
                    //reset lineExceedIndex
                    setLineExceedIndex(-1)
                }
            }
        } else {
            if (exceedsLineLimit) {
                setExceedsLineLimit(false)
                setLineExceedIndex(-1)
            }
        }
    }, [contentRef, exceedsLineLimit, limit, lineExceedIndex, lineHeight])

    /**
     * This method is called when the editor's width or height
     * changes. It updates the line colors.
     */
    const handleResize = () => {
        applyColors()
        wrapTextContents(
            decodeEntities(
                sanitizeHtml(contentRef?.textContent ?? '', stripperConfig)
            )
        )
    }

    React.useEffect(() => {
        if (!editable) {
            setCursorPosition(0)
        }
    }, [editable])

    React.useEffect(() => {
        if (content) {
            setContent(
                wrapTextContents(
                    decodeEntities(sanitizeHtml(content ?? '', stripperConfig))
                )
            )
        }
    }, [wrapTextContents, content])

    /**
     * This effect watches for changes to `content` state
     * and applies coloration (gray-out). It also repositions
     * the cursor because making DOM changes resets cursor
     * position to the end of editor, which isn't ideal
     * for when the user is working on texts in the middle part
     * of the content.
     */
    React.useEffect(() => {
        applyColors()
        if (cursorPosition) {
            positionCursor(cursorPosition)
        }
    }, [content, applyColors, cursorPosition, positionCursor])

    React.useEffect(() => {
        if (textContent !== prevTextContent.current) {
            const newContent = wrapTextContents(textContent)
            setContent(newContent)
        }
    }, [textContent, wrapTextContents])

    React.useEffect(() => {
        prevTextContent.current = textContent
    }, [textContent])

    /**
     * Toggles edit mode on click.
     */
    if (firmLoading) {
        return (
            <Skeleton
                width="80%"
                height="24px"
                data-testid="dvEditableTextLoadSkeleton"
            />
        )
    }

    return (
        <div className={classes.editableTextContainer} {...props}>
            {editable ? (
                <GridContainer direction="column">
                    <GridItem
                        xs
                        onBlur={handleBlur}
                        container
                        direction="column"
                        className={clsx(
                            'editableTextContentContainer',
                            classes.editableTextContentText,
                            classes.textEditMode,
                            (contentRef?.textContent ?? '').trim().length <=
                                0 && classes.textEditModeError
                        )}
                    >
                        <GridItem>
                            <div
                                ref={setContentRef}
                                className={editorClasses.editorDiv}
                            >
                                <ContentEditable
                                    html={`${content}`}
                                    onPaste={handleContentPaste}
                                    onChange={handleContentChange}
                                    onKeyDown={handleKeyDown}
                                    role="input"
                                    className={clsx(
                                        'editableTextEditorDiv',
                                        classes.baseInput
                                    )}
                                />
                            </div>
                            <ReactResizeDetector
                                skipOnMount
                                handleWidth
                                handleHeight
                                onResize={handleResize}
                            />
                        </GridItem>
                        <GridItem
                            container
                            justifyContent="flex-end"
                            alignItems="center"
                            className={clsx(
                                limit && classes.characterCountContainer,
                                (contentRef?.textContent ?? '').trim().length <=
                                    0 && classes.characterCountContainerError
                            )}
                        >
                            {(limit || 0) > 0 && exceedsLineLimit ? (
                                <Typography
                                    className={clsx(
                                        'editableTextWarningMessage',
                                        classes.characterCountText
                                    )}
                                >
                                    Only Two Lines Will Display
                                </Typography>
                            ) : (
                                <div
                                    className={classes.counterPlaceholder}
                                ></div>
                            )}
                        </GridItem>
                    </GridItem>
                    {contentRef &&
                    (contentRef?.textContent ?? '').trim().length <= 0 ? (
                        <GridItem container>
                            <Typography
                                variant="caption"
                                className={classes.errorMessage}
                            >
                                This field is required.
                            </Typography>
                        </GridItem>
                    ) : null}
                </GridContainer>
            ) : (
                <pre className={classes.normalizedFont}>
                    <TooltipEllipsis text={textContent} lineLimit={2} />
                </pre>
            )}
        </div>
    )
}

export default EditableText
